import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import '../styles/ResumeModal.css';
import { trackPortfolioEvent } from '../services/analytics';

const ResumeModal = ({ isOpen, onClose, resumeUrl, fileName = 'Naman_Sanadhya_Resume.pdf' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRendered, setIsRendered] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      setIsClosing(false);
      setIsLoading(true);
    } else if (isRendered) {
      setIsClosing(true);
      const timer = window.setTimeout(() => {
        setIsRendered(false);
        setIsClosing(false);
      }, 240);
      return () => window.clearTimeout(timer);
    }
  }, [isOpen, isRendered]);

  const handleRequestClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    window.setTimeout(() => {
      onClose();
    }, 220);
  }, [isClosing, onClose]);

  useEffect(() => {
    if (!isRendered || isClosing) {
      return undefined;
    }

    trackPortfolioEvent({
      eventType: 'resume_preview',
      targetKey: 'resume_modal',
      targetLabel: 'Resume Modal Preview',
      source: 'resume_preview_button'
    });

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleRequestClose();
      }
    };

    const handleWheel = (event) => {
      // Prevent background scrolling when scrolling on the overlay backdrop
      if (event.target && event.target.classList && event.target.classList.contains('resume-modal-overlay')) {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchmove', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleWheel);
    };
  }, [isRendered, isClosing, handleRequestClose]);

  if (!isRendered || !resumeUrl || typeof document === 'undefined') {
    return null;
  }

  const handleDownload = () => {
    trackPortfolioEvent({
      eventType: 'resume_download',
      targetKey: 'resume',
      targetLabel: 'Resume PDF',
      source: 'resume_modal_download'
    });
  };

  const modalContent = (
    <div
      className={`resume-modal-overlay ${isClosing ? 'is-closing' : ''}`}
      onClick={handleRequestClose}
      role="dialog"
      aria-modal="true"
      aria-label="Resume Preview Modal"
    >
      <div className="resume-modal-container" onClick={(e) => e.stopPropagation()}>
        <header className="resume-modal-header">
          <div className="resume-modal-title-group">
            <span className="resume-modal-badge">Resume</span>
            <div className="resume-modal-heading-text">
              <h3>Naman Sanadhya</h3>
              <span className="resume-modal-subtitle">Backend Engineer</span>
            </div>
          </div>

          <div className="resume-modal-actions">
            <a
              href={resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="resume-modal-btn secondary"
              title="Open full PDF in a new tab"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              <span>Full Screen</span>
            </a>

            <a
              href={resumeUrl}
              download={fileName}
              onClick={handleDownload}
              className="resume-modal-btn primary"
              title="Download PDF to device"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>Download PDF</span>
            </a>

            <button
              type="button"
              className="resume-modal-close"
              onClick={handleRequestClose}
              aria-label="Close resume preview (Esc)"
              title="Close (Esc)"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              <span className="resume-modal-close-label">Close</span>
            </button>
          </div>
        </header>

        <div className="resume-modal-body">
          {isLoading && (
            <div className="resume-modal-loader" aria-live="polite">
              <div className="resume-modal-spinner" />
              <span>Loading Resume Preview...</span>
            </div>
          )}
          <iframe
            src={`${resumeUrl}#toolbar=0&navpanes=0`}
            title="Naman Sanadhya Resume Preview"
            className="resume-modal-iframe"
            onLoad={() => setIsLoading(false)}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ResumeModal;
